import { BookmarksContext } from "context/bookmarks";
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { READER_THEMES } from 'utils/themes';
import { queuePropType, readerRefPropType } from 'utils/prop-types';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import Icons from 'components/icons';
import NavigationMenuWrapper from 'components/common/navigation-menu-wrapper';
import useStyles from './navigation-menu.styles';
import {
  loadTOCItems,
  loadAnnotationItems,
  locationToChapter,
  locationToAnnotation
} from './navigation-menu.utils';
import ChaptersTabPanel from "components/ebook-reader/components/navigation-menu/chapters-tab.component";
import AnnotationsTabPanel from "components/ebook-reader/components/navigation-menu/annotations-tab.component";
import BookmarksTabPanel from "components/ebook-reader/components/navigation-menu/bookmarks-tab.component";
import { getNavigationTabOptions, NAVIGATION_TABS } from "components/ebook-reader/ebook-reader.utils";


const TAB_OPTIONS = getNavigationTabOptions([
  NAVIGATION_TABS.chapters,
  NAVIGATION_TABS.bookmarks,
  NAVIGATION_TABS.annotation,
]);

const NavigationMenu = memo((props) => {
  const { queue, activeIndex, readerRef, activeTheme, controlBtnProps, annotationRefreshTrigger } = props;
  const { assetId } = queue[activeIndex] ?? {};
  const classes = useStyles({ activeTheme });
  const { t } = useTranslation();
  const { loadBookmarks } = useContext(BookmarksContext);

  const containerRef = useRef(null);
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const [isLoading, setIsLoading] = useState({
    [NAVIGATION_TABS.chapters]: false,
    [NAVIGATION_TABS.annotation]: false,
  });
  const [TOCItems, setTOCItems] = useState([]);
  const [annotationItems, setAnnotationItems] = useState([]);
  const [activeTab, setActiveTab] = useState(NAVIGATION_TABS.chapters);

  const handleNavItemClick = useCallback(
    (selector) => () => {
      readerRef.current.goTo(selector);
    },
    [],
  );

  const handleTabChange = useCallback((_, value) => {
    setActiveTab(value);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setIsNavigationOpen(false);
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      setActiveTab((currentTab) => {
        const currentIndex = TAB_OPTIONS.findIndex(({ value }) => value === currentTab);
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (currentIndex + direction + TAB_OPTIONS.length) % TAB_OPTIONS.length;

        return TAB_OPTIONS[nextIndex].value;
      });
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.stopPropagation();
    }
  }, []);

  /** load chapters **/
  const loadAndSetChapters = useCallback(() => {
    setIsLoading(prev => ({...prev, [NAVIGATION_TABS.chapters]: true}));
    loadTOCItems(readerRef).then((items) => {
      setTOCItems(items || []);
      setIsLoading(prev => ({ ...prev, [NAVIGATION_TABS.chapters]: false }));
    });
  }, []);

  /** load bookmarks **/
  const loadAndSetBookmarks = useCallback(() => {
    return loadBookmarks(assetId);
  }, [assetId]);

  /** load annotations **/
  const loadAndSetAnnotations = useCallback(() => {
    setIsLoading(prev => ({...prev, [NAVIGATION_TABS.annotation]: true}));
    loadAnnotationItems(readerRef).then((items) => {
      setAnnotationItems(items || []);
      setIsLoading(prev => ({ ...prev, [NAVIGATION_TABS.annotation]: false }));
    });
  }, []);

  /** load all items **/
  const loadAndSetNavigationItems = useCallback(() => {
    loadAndSetChapters();
    loadAndSetBookmarks();
    loadAndSetAnnotations();
  }, [loadAndSetChapters, loadAndSetBookmarks, loadAndSetAnnotations]);

  // Reload nav items whenever timeline changes, e.g. when the page numbers change
  const handleTimelineRecalculated = useCallback(() => {
    loadAndSetNavigationItems();
  }, []);

  useEffect(() => {
    loadAndSetNavigationItems();
    readerRef.current.addEventListener('timelineRecalculated', handleTimelineRecalculated);
  }, []);

  useEffect(() => {
    if (annotationRefreshTrigger > 0) {
      loadAndSetAnnotations();
    }
  }, [annotationRefreshTrigger, loadAndSetAnnotations]);

  useEffect(() => {
    if (!isNavigationOpen) {
      return;
    }
    window.requestAnimationFrame(() => {
      containerRef.current
        ?.querySelector('[tabindex]:not([tabindex="-1"])')
        ?.focus();
    });
  }, [isNavigationOpen]);

  return (
  <NavigationMenuWrapper
    controlBtnProps={{ ...controlBtnProps, 'aria-label': t('reader_icon_toc'), title: t('reader_icon_toc') }}
    paperProps={{ className: classes.paper }}
    controlBtnIcon={Icons.ReaderTOC}
    onNavigationOpenChange={setIsNavigationOpen}
    isNavigationOpen={isNavigationOpen}
  >
    <Box
      ref={containerRef}
      className={classes.container}
      onKeyDown={handleKeyDown}
    >
      <Box className={classes.tabHeader}>
        <Tabs
          className={classes.tabs}
          indicatorColor="primary"
          variant="fullWidth"
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ className: classes.tabIndicator }}
        >
          {TAB_OPTIONS.map(({ value, label, ...restProps }) => (
            <Tab key={value} value={value} label={t(label)} {...restProps} />
          ))}
        </Tabs>
      </Box>
      {TAB_OPTIONS.map(({ value: tab }) => {
        if (tab === NAVIGATION_TABS.chapters) {
          return <ChaptersTabPanel
              classes={classes}
              activeTab={activeTab}
              isLoading={isLoading[NAVIGATION_TABS.chapters]}
              items={TOCItems.map(locationToChapter)}
              handleNavItemClick={handleNavItemClick}
            />
        }
        if (tab === NAVIGATION_TABS.bookmarks) {
          return <BookmarksTabPanel
              activeTab={activeTab}
              classes={classes}
              handleNavItemClick={handleNavItemClick}
              readerRef={readerRef}
          />
        }
        if (tab === NAVIGATION_TABS.annotation) {
          return <AnnotationsTabPanel
              classes={classes}
              activeTab={activeTab}
              activeTheme={activeTheme}
              isLoading={isLoading[NAVIGATION_TABS.annotation]}
              items={annotationItems.map(locationToAnnotation).sort((a, b) => a.page - b.page)}
              handleNavItemClick={handleNavItemClick}
          />
        }
      })}
    </Box>
  </NavigationMenuWrapper>
  );
});

NavigationMenu.propTypes = {
  queue: queuePropType,
  activeIndex: PropTypes.number.isRequired,
  readerRef: readerRefPropType,
  activeTheme: PropTypes.oneOf(Object.keys(READER_THEMES)),
  controlBtnProps: PropTypes.shape({
    className: PropTypes.string,
    iconClassName: PropTypes.string,
  }),
  annotationRefreshTrigger: PropTypes.number,
};

export default NavigationMenu;
